import React, { useMemo, useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Typography,
  Statistic,
  DatePicker,
  Progress,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import {
  SwapOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SyncOutlined,
  InboxOutlined,
  WarningOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { AedDirhamSign } from '@/components/currency';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useItemsWithStock, useStockMovements, useWarehouses } from '@/hooks/useInventory';
import type { ItemWithStock, StockMovementRow } from '@/services/inventory.service';
import {
  CriticalItemTag,
  getCriticalItemRowClassName,
  isItemCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import { useAiListSelection } from '@/hooks/useAiListSelection';

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

interface StockSummaryRow {
  id: string;
  key: string;
  itemCode: string;
  itemName: string;
  category: string;
  currentStock: number;
  reservedStock: number;
  freeStock: number;
  reorderLevel: number;
  maxStock: number;
  uom: string;
  unitPrice: number;
  amount: number;
  warehouse: string;
  is_critical: boolean;
}

function movementTypeColor(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('purchase') || t.includes('opening') || t.includes('receipt') || t.includes('return') || t.includes('foc')) {
    return 'green';
  }
  if (t.includes('issue') || t.includes('sales')) return 'blue';
  if (t.includes('transfer')) return 'orange';
  if (t.includes('adjustment')) return 'purple';
  if (t.includes('reversal')) return 'red';
  return 'default';
}

function movementTypeIcon(type: string): React.ReactNode {
  const t = type.toLowerCase();
  if (t.includes('transfer')) return <SwapOutlined />;
  if (t.includes('adjustment')) return <SyncOutlined />;
  if (
    t.includes('issue') ||
    t.includes('sales') ||
    t.includes('out') ||
    (t.includes('return') && t.includes('purchase'))
  ) {
    return <ArrowUpOutlined />;
  }
  return <ArrowDownOutlined />;
}

const KNOWN_MOVEMENT_TYPES = [
  'Purchase',
  'Opening',
  'Material Issue',
  'Material Return',
  'Transfer In',
  'Transfer Out',
  'Adjustment In',
  'Adjustment Out',
  'Production Receipt',
  'GRN Reversal',
  'Purchase Return',
  'Owner Supplied FOC',
  'Subcon Free Issue',
  'Intercompany Transfer',
];

function mapStockRow(item: ItemWithStock, warehouseLabel: string): StockSummaryRow {
  const qty = Number(item.available_qty ?? item.current_stock ?? 0) || 0;
  const reserved = Number(item.reserved_qty ?? 0) || 0;
  const free =
    item.free_quantity != null
      ? Math.max(0, Number(item.free_quantity) || 0)
      : Math.max(0, qty - reserved);
  const amount = Number(item.amount ?? item.total_value ?? 0) || 0;
  const unitPrice =
    Number(item.unit_price ?? item.average_cost ?? 0) || (qty > 0 ? amount / qty : 0);
  const maxStock = Number(item.maximum_stock_level ?? 0) || 0;

  return {
    id: item.id,
    key: item.id,
    itemCode: item.item_code || '',
    itemName: item.item_name || '',
    category: item.category_name || '—',
    currentStock: qty,
    reservedStock: reserved,
    freeStock: free,
    reorderLevel: Number(item.reorder_level ?? 0) || 0,
    maxStock: maxStock > 0 ? maxStock : Math.max(qty, 1),
    uom: item.base_uom || '',
    unitPrice,
    amount,
    warehouse: item.warehouse_name || warehouseLabel,
    is_critical: isItemCritical(item),
  };
}

export const StockManagementPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const [searchText, setSearchText] = useState('');
  const [stockFilter, setStockFilter] = useState<'all' | 'low-stock' | 'out-of-stock'>('all');
  const [warehouseId, setWarehouseId] = useState<string | undefined>();
  const [movementType, setMovementType] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'movements'>('summary');

  const { data: warehouses = [] } = useWarehouses();
  const { data: stockItems = [], isLoading: stockLoading } = useItemsWithStock(warehouseId);
  const { isCriticalCode } = useCriticalItemLookup(activeTab === 'movements');

  const movementFilters = useMemo(
    () => ({
      warehouse_id: warehouseId,
      transaction_type: movementType,
      from_date: dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
      to_date: dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      search: activeTab === 'movements' ? searchText.trim() || undefined : undefined,
      limit: 500,
    }),
    [warehouseId, movementType, dateRange, searchText, activeTab]
  );

  const { data: movements = [], isLoading: movementsLoading } = useStockMovements(movementFilters);
  const { rowSelection: movementRowSelection } = useAiListSelection({
    entityType: 'StockMovement',
    module: 'inventory',
    listFilters: movementFilters as Record<string, unknown>,
    labelById: (id) => {
      const m = movements.find((x) => x.id === id);
      return m ? `${m.item_code || ''} ${m.transaction_type || ''}`.trim() : undefined;
    },
  });

  const warehouseLabel = useMemo(() => {
    if (!warehouseId) return 'All Warehouses';
    const wh = warehouses.find((w) => w.id === warehouseId);
    return wh?.warehouse_name || wh?.warehouse_code || 'Warehouse';
  }, [warehouseId, warehouses]);

  const stockSummary = useMemo(
    () => stockItems.map((item) => mapStockRow(item, warehouseLabel)),
    [stockItems, warehouseLabel]
  );

  const filteredSummary = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return stockSummary.filter((item) => {
      if (stockFilter === 'low-stock' && item.currentStock > item.reorderLevel) return false;
      if (stockFilter === 'out-of-stock' && item.currentStock > 0) return false;
      if (!q) return true;
      return (
        item.itemCode.toLowerCase().includes(q) ||
        item.itemName.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q)
      );
    });
  }, [stockSummary, searchText, stockFilter]);

  const movementTypeOptions = useMemo(() => {
    const set = new Set<string>(KNOWN_MOVEMENT_TYPES);
    for (const m of movements) {
      if (m.transaction_type) set.add(m.transaction_type);
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ value: v, label: v }));
  }, [movements]);

  const summaryColumns: ColumnsType<StockSummaryRow> = [
    {
      title: 'Item Code',
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 160,
      render: (code: string, record) => (
        <Space size={6} wrap>
          <span>{code}</span>
          <CriticalItemTag critical={record.is_critical} />
        </Space>
      ),
    },
    {
      title: 'Item Name',
      dataIndex: 'itemName',
      key: 'itemName',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
    },
    {
      title: 'On hand',
      key: 'currentStock',
      width: 130,
      render: (_, record) => {
        const isLow = record.reorderLevel > 0 && record.currentStock <= record.reorderLevel;
        const stockPercent =
          record.maxStock > 0 ? (record.currentStock / record.maxStock) * 100 : 0;

        return (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Space>
              <Text
                strong
                style={{ color: isLow || record.currentStock <= 0 ? '#f5222d' : '#52c41a' }}
              >
                {record.currentStock} {record.uom}
              </Text>
              {isLow && <WarningOutlined style={{ color: '#f5222d' }} />}
            </Space>
            <Progress
              percent={Math.min(100, Math.round(stockPercent))}
              size="small"
              status={isLow || record.currentStock <= 0 ? 'exception' : 'success'}
              showInfo={false}
            />
          </Space>
        );
      },
    },
    {
      title: 'Reserved',
      key: 'reservedStock',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Text type={record.reservedStock > 0 ? 'warning' : 'secondary'}>
          {record.reservedStock} {record.uom}
        </Text>
      ),
    },
    {
      title: 'Free',
      key: 'freeStock',
      width: 110,
      align: 'right',
      render: (_, record) => (
        <Text strong style={{ color: record.freeStock <= 0 ? '#f5222d' : undefined }}>
          {record.freeStock} {record.uom}
        </Text>
      ),
    },
    {
      title: 'Reorder Level',
      dataIndex: 'reorderLevel',
      key: 'reorderLevel',
      width: 120,
      align: 'right',
      render: (level, record) => `${level} ${record.uom}`,
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right',
      render: (value: number) => formatCurrency(Number(value) || 0),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (value: number) => <Text strong>{formatCurrency(Number(value) || 0)}</Text>,
    },
    {
      title: 'Warehouse',
      dataIndex: 'warehouse',
      key: 'warehouse',
      width: 150,
    },
  ];

  const movementColumns: ColumnsType<StockMovementRow> = [
    {
      title: 'Date & Time',
      dataIndex: 'transaction_time',
      key: 'transaction_time',
      width: 150,
      render: (value: string | null, record) => {
        const raw = value || record.transaction_date;
        return raw ? dayjs(raw).format('DD-MMM-YYYY HH:mm') : '—';
      },
      sorter: (a, b) => {
        const ta = new Date(a.transaction_time || a.transaction_date || 0).getTime();
        const tb = new Date(b.transaction_time || b.transaction_date || 0).getTime();
        return ta - tb;
      },
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      width: 150,
      render: (type: string) => (
        <Tag color={movementTypeColor(type)} icon={movementTypeIcon(type)}>
          {type || '—'}
        </Tag>
      ),
    },
    {
      title: 'Item',
      key: 'item',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space size={6} wrap>
            <Text strong>{record.item_code}</Text>
            <CriticalItemTag critical={isCriticalCode(record.item_code)} />
          </Space>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.item_name}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Quantity',
      key: 'quantity',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const qty = Number(record.quantity) || 0;
        const isPositive = qty > 0;
        return (
          <Text strong style={{ color: isPositive ? '#52c41a' : '#f5222d' }}>
            {isPositive ? '+' : ''}
            {qty} {record.uom}
          </Text>
        );
      },
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right',
      render: (value: number) => formatCurrency(Number(value) || 0),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (value: number) => {
        const amt = Number(value) || 0;
        return (
          <Text strong style={{ color: amt < 0 ? '#f5222d' : undefined }}>
            {formatCurrency(amt)}
          </Text>
        );
      },
    },
    {
      title: 'From → To',
      key: 'locations',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            From: {record.from_location}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            To: {record.to_location}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Reference',
      key: 'reference',
      width: 140,
      render: (_, record) => record.voucher_number || record.voucher_type || '—',
    },
    {
      title: 'User',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 120,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Notes',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
      render: (v: string | null) => v || '—',
    },
  ];

  const totalStockValue = filteredSummary.reduce((sum, item) => sum + item.amount, 0);
  const lowStockItems = stockSummary.filter(
    (item) => item.reorderLevel > 0 && item.currentStock <= item.reorderLevel
  ).length;
  const totalMovementsToday = movements.filter((m) => {
    const raw = m.transaction_time || m.transaction_date;
    return raw ? dayjs(raw).isSame(dayjs(), 'day') : false;
  }).length;

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Stock Management</Title>
        <Text type="secondary">Real-time inventory tracking and stock movements</Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Stock Value"
              value={totalStockValue}
              prefix={<AedDirhamSign />}
              valueStyle={{ color: '#145A54' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Items"
              value={filteredSummary.length}
              prefix={<InboxOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Low Stock Alert"
              value={lowStockItems}
              prefix={<WarningOutlined />}
              valueStyle={{ color: lowStockItems > 0 ? '#faad14' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Movements Today"
              value={totalMovementsToday}
              prefix={<HistoryOutlined />}
              valueStyle={{ color: '#145A54' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space style={{ marginBottom: '16px' }}>
          <Button
            type={activeTab === 'summary' ? 'primary' : 'default'}
            onClick={() => setActiveTab('summary')}
          >
            Stock Summary
          </Button>
          <Button
            type={activeTab === 'movements' ? 'primary' : 'default'}
            onClick={() => setActiveTab('movements')}
          >
            Stock Movements
          </Button>
        </Space>

        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <Space wrap>
            <Search
              placeholder={activeTab === 'summary' ? 'Search items...' : 'Search movements...'}
              allowClear
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {activeTab === 'summary' ? (
              <>
                <Select
                  value={stockFilter}
                  style={{ width: 150 }}
                  onChange={(v) => setStockFilter(v)}
                  options={[
                    { value: 'all', label: 'All Items' },
                    { value: 'low-stock', label: 'Low Stock' },
                    { value: 'out-of-stock', label: 'Out of Stock' },
                  ]}
                />
                <Select
                  allowClear
                  placeholder="All Warehouses"
                  style={{ width: 200 }}
                  value={warehouseId}
                  onChange={(v) => setWarehouseId(v || undefined)}
                  options={warehouses.map((w) => ({
                    value: w.id,
                    label: w.warehouse_name || w.warehouse_code,
                  }))}
                />
              </>
            ) : (
              <>
                <Select
                  allowClear
                  placeholder="All Types"
                  style={{ width: 180 }}
                  value={movementType}
                  onChange={(v) => setMovementType(v || undefined)}
                  options={movementTypeOptions}
                />
                <Select
                  allowClear
                  placeholder="All Warehouses"
                  style={{ width: 200 }}
                  value={warehouseId}
                  onChange={(v) => setWarehouseId(v || undefined)}
                  options={warehouses.map((w) => ({
                    value: w.id,
                    label: w.warehouse_name || w.warehouse_code,
                  }))}
                />
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                  allowClear
                />
              </>
            )}
          </Space>
        </div>

        {activeTab === 'summary' ? (
          <Table
            columns={summaryColumns}
            dataSource={filteredSummary}
            loading={stockLoading}
            rowClassName={(record) => getCriticalItemRowClassName(record)}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} items`,
            }}
            size="small"
            scroll={{ x: 1100 }}
          />
        ) : (
          <Table
            rowKey="id"
            columns={movementColumns}
            dataSource={movements}
            loading={movementsLoading}
            rowSelection={movementRowSelection}
            rowClassName={(record) =>
              getCriticalItemRowClassName({ is_critical: isCriticalCode(record.item_code) })
            }
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} movements`,
            }}
            size="small"
            scroll={{ x: 1400 }}
          />
        )}
      </Card>
    </div>
  );
};
